// One function per endpoint the pages use. Paths are relative to /api/v1 and
// match the OpenAPI snapshot; every response is mapped to the view model by
// the pure adapters in ./adapt.ts.
import {
  toBalances,
  toCandidateList,
  toCandlePrice,
  toClosedLoansView,
  toDealDetail,
  toDealSummary,
  toEventRow,
  toEventsPage,
  toLocationItem,
  toLoanLine,
  toLoanListView,
  toRepaySuggestion,
  toMarkOutcome,
  toMergeSuggestion,
  toOverview,
} from './adapt';
import { apiFetch, logoutRequest } from './client';
import type { Balances, CandidateList, CandidateQuery, CandlePrice, ClosedLoansView, ClosedQuery, DealDetail, DealSummary, EventRow, EventsPage, EventsQuery, LoanLine, LoanListView, LoanQuery, RepaySuggestionView, RepaySuggestionQuery, LocationItem, MarkOutcome, MergeSuggestion, Me, NewDeal, Overview } from './model';
import type * as W from './types';

export const getMe = async (signal?: AbortSignal): Promise<Me | null> => {
  const me = await apiFetch<W.MeOut>('/auth/me', { signal });
  return me.authenticated && me.sub ? { sub: me.sub, email: me.email, csrf: me.csrf } : null;
};
export const postLogout = () => logoutRequest();

export const getOverview = async (signal?: AbortSignal): Promise<Overview> => toOverview(await apiFetch<W.Overview>('/overview', { signal }));

export const getCandidates = async (q: CandidateQuery, signal?: AbortSignal): Promise<CandidateList> =>
  toCandidateList(await apiFetch<W.CandidateList>('/binance/candidates', { query: { ...q }, signal }), q.account);

export const postMark = async (body: W.MarkRequest): Promise<MarkOutcome> => toMarkOutcome(await apiFetch<W.MarkResult>('/events/mark', { method: 'POST', body }));
export const postManualEvent = async (body: W.ManualEventIn): Promise<EventRow> => toEventRow(await apiFetch<W.EventOut>('/events/manual', { method: 'POST', body }));
export const postVoidEvent = async (id: string, reason = ''): Promise<EventRow> =>
  toEventRow(await apiFetch<W.EventOut>(`/events/${id}/void`, { method: 'POST', body: reason ? ({ reason } satisfies W.VoidIn) : undefined }));
/** Assign an already recorded event to deals afterwards: replaces the whole allocation set (append-only underneath); [] = remove from every deal. */
export const putEventAllocations = async (id: string, allocations: W.AllocationIn[]): Promise<EventRow> =>
  toEventRow(await apiFetch<W.EventOut>(`/events/${id}/allocations`, { method: 'PUT', body: { allocations } satisfies W.AllocationsPut }));
export const getEvents = async (query: { deal_id?: number; location_id?: string; type?: string; from?: string; to?: string }, signal?: AbortSignal): Promise<EventRow[]> =>
  (await apiFetch<W.EventList>('/events', { query, signal })).items.map(toEventRow);

/** D43: the filterable, sortable, paged event table. Repeatable parameters (source, type) are sent once per value. */
export const getEventsPage = async (q: EventsQuery, signal?: AbortSignal): Promise<EventsPage> =>
  toEventsPage(await apiFetch<W.EventList>('/events', { query: { ...q }, signal }));
export const patchAnnotation = async (id: string, body: W.AnnotationIn): Promise<EventRow> =>
  toEventRow(await apiFetch<W.EventOut>(`/events/${id}/annotation`, { method: 'PATCH', body }));

export const getDeals = async (signal?: AbortSignal): Promise<DealSummary[]> => (await apiFetch<W.DealList>('/deals', { signal })).items.map(toDealSummary);
export const getDeal = async (id: number, signal?: AbortSignal): Promise<DealDetail> => toDealDetail(await apiFetch<W.DealDetail>(`/deals/${id}`, { signal }));
export const postDeal = (body: NewDeal) => apiFetch<W.DealOut>('/deals', { method: 'POST', body });
export const postCloseDeal = (id: number, allowOpenLoans = false) =>
  apiFetch<W.DealOut>(`/deals/${id}/close`, { method: 'POST', body: allowOpenLoans ? ({ allow_open_loans: true } satisfies W.DealCloseIn) : undefined });

export const getLocationItems = async (signal?: AbortSignal): Promise<LocationItem[]> =>
  (await apiFetch<W.LocationList>('/locations', { signal })).items.map(toLocationItem);
export const postLocation = async (body: W.LocationNew): Promise<LocationItem> => toLocationItem(await apiFetch<W.LocationOut>('/locations', { method: 'POST', body }));
export const getBalances = async (signal?: AbortSignal): Promise<Balances> => toBalances(await apiFetch<W.BalanceMatrix>('/locations/balances', { signal }));

export const getMergeSuggestions = async (signal?: AbortSignal): Promise<MergeSuggestion[]> =>
  (await apiFetch<W.MergeList>('/merge-suggestions', { signal })).items.map(toMergeSuggestion);
export const postMergeDecision = async (id: string, decision: 'accept' | 'reject'): Promise<MergeSuggestion> =>
  toMergeSuggestion(await apiFetch<W.MergeSuggestionOut>(`/merge-suggestions/${id}/${decision}`, { method: 'POST' }));

export const getCandlePrice = async (asset: string, at: string, signal?: AbortSignal): Promise<CandlePrice> =>
  toCandlePrice(await apiFetch<W.CandlePrice>('/prices/candle', { query: { asset, at }, signal }));

/** D40: one row per live borrow event. */
export const getLoans = async (q: LoanQuery, signal?: AbortSignal): Promise<LoanLine[]> =>
  (await apiFetch<W.LoanList>('/loans', { query: { ...q }, signal })).items.map(toLoanLine);
/** D47: the borrows PLUS the repays still waiting for a borrow (per location + coin). */
export const getLoanList = async (q: LoanQuery, signal?: AbortSignal): Promise<LoanListView> =>
  toLoanListView(await apiFetch<W.LoanList>('/loans', { query: { ...q }, signal }));
/** D46: a PROPOSAL ("legkedvezőbb először"); nothing is applied server-side. */
export const getRepaySuggestion = async (q: RepaySuggestionQuery, signal?: AbortSignal): Promise<RepaySuggestionView> =>
  toRepaySuggestion(await apiFetch<W.RepaySuggestion>('/loans/repay-suggestion', { query: { ...q }, signal }));
/** D46: replace the set of links of a repay; [] = unassigned again. D50: optionally change the interest part in place and demand a full close. */
export const putRepayLinks = async (repayId: string, links: W.RepayLinkIn[], opts: { interest_quantity?: string; close?: boolean } = {}): Promise<EventRow> =>
  toEventRow(await apiFetch<W.EventOut>(`/events/${repayId}/repay-links`, {
    method: 'PUT',
    body: {
      links,
      ...(opts.interest_quantity !== undefined ? { interest_quantity: opts.interest_quantity } : {}),
      ...(opts.close ? { close: true } : {}),
    } satisfies W.RepayLinksPut,
  }));
/** D49: a hand-entered price level (source `manual`). */
export const putPriceLevel = async (eventId: string, price: string): Promise<EventRow> =>
  toEventRow(await apiFetch<W.EventOut>(`/events/${eventId}/price-level`, { method: 'PUT', body: { price } satisfies W.PriceLevelPut }));
/** Owner action: ask the price chain (Candle, then Binance public) again. A manual price answers 409 `manual_price_protected` unless `force`. */
export const postRecomputePriceLevel = async (eventId: string, force = false): Promise<EventRow> =>
  toEventRow(await apiFetch<W.EventOut>(`/events/${eventId}/price-level/recompute`, { method: 'POST', body: { force } satisfies W.PriceLevelRecomputeIn }));

/** D51: mark the UNASSIGNED part of a repay as paying a borrow from before the history; a second PUT replaces the note. */
export const putPrehistory = async (repayId: string, note = ''): Promise<EventRow> =>
  toEventRow(await apiFetch<W.EventOut>(`/events/${repayId}/prehistory`, { method: 'PUT', body: { note } satisfies W.PrehistoryPut }));
/** D51: take the pre-history mark off (the repay is back in "Hozzárendelésre vár"). */
export const deletePrehistory = async (repayId: string): Promise<EventRow> =>
  toEventRow(await apiFetch<W.EventOut>(`/events/${repayId}/prehistory`, { method: 'DELETE' }));
/** D52: the closed-loans report (UTC dates, inclusive; totals cover the whole filtered set). */
export const getClosedLoans = async (q: ClosedQuery, signal?: AbortSignal): Promise<ClosedLoansView> =>
  toClosedLoansView(await apiFetch<W.ClosedLoanList>('/loans/closed', { query: { ...q }, signal }));
